import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFullReset, useKioskState } from '../../../context/Kiosk/kiosk';
import { createStripePaymentSession } from '../../../API/GoMoto';
import { useLocalization } from '../../../context/Localization/localization';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { Spacer } from '../../../controls/Spacer/Spacer';
import css from './StripePayment.module.scss';
import { useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { DealerFooter } from './DealerFooter';
import { Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { PaymentProvider, DealerStatus } from '../../../API/Enums';
import { GMDialogError } from '../../../controls/Dialog';
import { loadStripe } from '@stripe/stripe-js/pure';

interface IProps {
    onSkip: (stripeSessionId?: string) => void;
    onReload: () => void;
}

export const StripePayment: React.FC<IProps> = ({ onSkip, onReload }) => {
    const appointment = useAppointmentStateUnsafe();
    const Localization = useLocalization();
    const {
        dealer: { key: dealerKey, paymentAccount, onlinePaymentsActive, paymentProvider, status },
    } = useKioskState();
    const reset = useFullReset(true, false);
    const [showPaymentErrorDialog, setShowPaymentErrorDialog] = useState<boolean>(false);
    const [clientSecret, setClientSecret] = useState<string>('');
    const stripePromise = useMemo<Promise<Stripe | null>>(() => {
        if (paymentAccount) {
            return loadStripe(
                (status === DealerStatus.Test ? (process.env.REACT_APP_STRIPE_KEY_TEST ?? process.env.REACT_APP_STRIPE_KEY) : process.env.REACT_APP_STRIPE_KEY) || '',
                { stripeAccount: paymentAccount },
            );
        }

        return Promise.resolve(null);
    }, [paymentAccount, status]);
    const sessionCreated = useRef<boolean>(false);

    useEffect(() => {
        if (!appointment || !onlinePaymentsActive || paymentProvider === PaymentProvider.None) {
            reset();
        }
        else if (appointment.paymentStatus === 'no payment due' || appointment.paymentStatus === 'marked paid') {
            onSkip();
        }
        else if (!sessionCreated.current) {
            sessionCreated.current = true;

            createStripePaymentSession(appointment.key, dealerKey)
                .then((data) => {
                    if (data === null) {
                        /*
                         * If null is returned the appointment payment status was 'marked as paid' or 'no payment due'.
                         * Reload so the user is informed of the appointment's change in status
                         */
                        onReload();
                    }
                    else if (data.status === 'complete') {
                        // The payment was already completed
                        onSkip(data.id);
                    }
                    else {
                        setClientSecret(data.clientSecret);
                    }
                })
                .catch(() => setShowPaymentErrorDialog(true));
        }

    }, [appointment, dealerKey, onReload, onSkip, onlinePaymentsActive, paymentProvider, reset]);

    return (
        <div className={css.payment}>
            <GMDialogError
                ariaLabel={Localization.FailedToEstablishPaymentSession}
                open={showPaymentErrorDialog}
                onClose={reset}
            >
                {Localization.FailedToEstablishPaymentSession}
            </GMDialogError>
            {!appointment ||
                appointment.paymentStatus === 'no payment due' ||
                appointment.paymentStatus === 'marked paid' ||
                !onlinePaymentsActive ||
                paymentProvider === PaymentProvider.None ?
                (
                    <Spinner />
                ) : (
                    <>
                        <p>
                            <GMAppointmentName appointment={appointment} />
                        </p>
                        <br />
                        <p>{Localization.PleaseConfirmServiceDetails}</p>
                        <br />
                        <Spacer />
                        <br />
                        <div className={css.form}>
                            {!clientSecret ? (
                                <Spinner />
                            ) : (
                                <EmbeddedCheckoutProvider
                                    stripe={stripePromise}
                                    options={{
                                        clientSecret: clientSecret,
                                    }}
                                >
                                    <EmbeddedCheckout />
                                </EmbeddedCheckoutProvider>
                            )
                            }
                        </div>
                        <DealerFooter />
                    </>
                )}
        </div>
    );
};
